/**
 * Money as integer cents, ZAR.
 *
 * There is exactly one currency in this system and no float ever touches a
 * price. The decimal point exists at the parse boundary and the format
 * boundary, and nowhere in between.
 *
 * See CLAUDE.md, "Conventions", and docs/02-data-model.md.
 */
import { err, ok } from './result.js';
import { thousandthsOf, QUANTITY_SCALE } from './quantity.js';
export const ZERO_CENTS = 0;
/**
 * The rounding rule for every derived amount in the system: round half away
 * from zero. This is the commercial convention South African invoicing expects,
 * and it is stated once here so that line totals, discounts and tax all agree
 * rather than each picking a rule at its call site.
 *
 * Banker's rounding is deliberately not used: it is correct for statistics and
 * surprising on an invoice a customer is checking by hand.
 */
export function roundHalfAwayFromZero(numerator, denominator) {
    if (denominator <= 0n) {
        throw new RangeError('denominator must be positive');
    }
    const negative = numerator < 0n;
    const magnitude = negative ? -numerator : numerator;
    const quotient = magnitude / denominator;
    const remainder = magnitude % denominator;
    const rounded = remainder * 2n >= denominator ? quotient + 1n : quotient;
    return negative ? -rounded : rounded;
}
function toCents(value, context) {
    if (value > BigInt(Number.MAX_SAFE_INTEGER) ||
        value < BigInt(-Number.MAX_SAFE_INTEGER)) {
        throw new RangeError(`${context} overflowed the safe integer range: ${String(value)}`);
    }
    return Number(value);
}
/** Build an amount from an exact count of cents. */
export function cents(value) {
    if (!Number.isSafeInteger(value)) {
        throw new TypeError(`cents() expects a safe integer, got ${value}. A fractional cent means a float reached a price.`);
    }
    return value;
}
/** Build an amount from a whole number of rand. */
export function rand(value) {
    if (!Number.isInteger(value)) {
        throw new TypeError(`rand() expects a whole number, got ${value}. Use parseAmount for fractional input.`);
    }
    return cents(value * 100);
}
/**
 * Parse a decimal string into cents. Deliberately strict: no currency symbol,
 * no thousands separators, at most two decimal places, no exponent notation.
 *
 * Anything looser has to guess whether "1,234" is one thousand two hundred and
 * thirty four or one point two three four, and guessing wrong on a price list
 * is not a recoverable mistake. Import paths normalise before they reach here
 * and report what they could not normalise.
 */
export function parseAmount(input) {
    const trimmed = input.trim();
    if (/[\s,]/.test(trimmed)) {
        return err({ kind: 'grouped-digits', input });
    }
    const match = /^([+-]?)(\d+)(?:\.(\d+))?$/.exec(trimmed);
    if (!match) {
        return err({ kind: 'not-a-number', input });
    }
    const [, sign = '', whole = '', fraction] = match;
    if (fraction !== undefined && fraction.length > 2) {
        return err({ kind: 'too-many-decimals', input, max: 2 });
    }
    const padded = (fraction ?? '').padEnd(2, '0');
    const magnitude = Number(whole) * 100 + Number(padded);
    if (!Number.isSafeInteger(magnitude)) {
        return err({ kind: 'out-of-range', input });
    }
    return ok((sign === '-' ? -magnitude : magnitude));
}
export function addCents(a, b) {
    return toCents(BigInt(a) + BigInt(b), 'addCents');
}
export function subtractCents(a, b) {
    return toCents(BigInt(a) - BigInt(b), 'subtractCents');
}
export function negateCents(value) {
    return toCents(-BigInt(value), 'negateCents');
}
export function sumCents(amounts) {
    let total = 0n;
    for (const amount of amounts)
        total += BigInt(amount);
    return toCents(total, 'sumCents');
}
export function compareCents(a, b) {
    if (a < b)
        return -1;
    if (a > b)
        return 1;
    return 0;
}
/**
 * Line total: a unit price times a fractional quantity.
 *
 * The multiplication runs in BigInt because a large price times a large
 * quantity leaves the safe integer range before it is scaled back down, and a
 * silently imprecise line total is exactly the failure this module exists to
 * prevent.
 */
export function multiplyByQuantity(unitPrice, quantity) {
    const scaled = BigInt(unitPrice) * BigInt(thousandthsOf(quantity));
    return toCents(roundHalfAwayFromZero(scaled, BigInt(QUANTITY_SCALE)), 'multiplyByQuantity');
}
/**
 * A percentage expressed in basis points, so 12.5% is 1250. Discounts and tax
 * rates are frequently fractional and storing them as a float reintroduces the
 * problem this module solves.
 */
export function percentOf(amount, basisPoints) {
    if (!Number.isSafeInteger(basisPoints)) {
        throw new TypeError(`basisPoints must be a safe integer, got ${basisPoints}`);
    }
    const scaled = BigInt(amount) * BigInt(basisPoints);
    return toCents(roundHalfAwayFromZero(scaled, 10000n), 'percentOf');
}
/** Plain decimal form for data interchange and tests: `-1234.05`. */
export function formatAmount(amount) {
    const negative = amount < 0;
    const magnitude = Math.abs(amount);
    const whole = Math.trunc(magnitude / 100);
    const fraction = magnitude % 100;
    return `${negative ? '-' : ''}${String(whole)}.${String(fraction).padStart(2, '0')}`;
}
/**
 * Display form: `R 1 234,56`.
 *
 * Written out rather than delegated to Intl.NumberFormat because Intl output
 * shifts with the host ICU version and inserts a non-breaking space, which
 * would make label golden files fail on a different machine and would reach a
 * thermal printer as a character it cannot encode.
 */
export function formatZAR(amount) {
    const negative = amount < 0;
    const magnitude = Math.abs(amount);
    const whole = String(Math.trunc(magnitude / 100));
    const fraction = String(magnitude % 100).padStart(2, '0');
    const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    return `${negative ? '-' : ''}R ${grouped},${fraction}`;
}
//# sourceMappingURL=money.js.map